import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { Formik, FormikValues, useFormikContext } from 'formik';

import FIELD_COMPONENT_BY_TYPE from './Fields';
import useTaskFormSubmit from './useTaskFormSubmit';

import ChevronLeftIcon from 'modules/common/components/Icons/ChevronLeftIcon/ChevronLeftIcon';
import LinkifyText from 'modules/common/components/LinkifyText';
import Dialog from 'modules/common/components/MUI/Dialog';
import { FormField, FormTaskContext, SubmitActionTask } from 'modules/Tasks/types';

// ---- Interfaces ----

interface FormModalProps {
    open: boolean;
    onClose: () => void;
    task: SubmitActionTask;
}

interface FormModalContentProps {
    open: boolean;
    fields: FormField[];
    heading: string;
    currentPage: number;
    totalPages: number;
    onNext: (values: FormikValues) => void;
    onBack: () => void;
    onClose: (values: FormikValues) => void;
}

interface FormModalFooterProps {
    currentPage: number;
    totalPages: number;
    canGoNext: boolean;
    isSubmitting: boolean;
    onBack: () => void;
    onNext: () => void;
    onSubmit: () => void;
}

function isFormTaskContext(ctx: unknown): ctx is FormTaskContext {
    return typeof ctx === 'object' && ctx !== null && Array.isArray((ctx as FormTaskContext).fields);
}

// ---- Components ----

function FormModalFooter({
    currentPage,
    totalPages,
    canGoNext,
    isSubmitting,
    onBack,
    onNext,
    onSubmit,
}: Readonly<FormModalFooterProps>) {
    const isFirstPage = currentPage === 0;
    const isLastPage = currentPage === totalPages - 1;
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            {isFirstPage ? (
                <Box />
            ) : (
                <IconButton aria-label="Back" onClick={onBack} sx={{ ml: -1 }}>
                    <ChevronLeftIcon fontSize="small" />
                </IconButton>
            )}
            <Stack direction="row" spacing={3} alignItems="center">
                {totalPages > 1 && (
                    <Typography variant="h5" color="text.secondary">
                        {currentPage + 1}/{totalPages}
                    </Typography>
                )}
                <Button
                    variant="contained"
                    size="large"
                    loading={isSubmitting}
                    onClick={isLastPage ? onSubmit : onNext}
                    disabled={!canGoNext || isSubmitting}>
                    {isLastPage ? 'Submit' : 'Next'}
                </Button>
            </Stack>
        </Box>
    );
}

function FormModalContent({
    open,
    fields,
    heading,
    currentPage,
    totalPages,
    onNext,
    onBack,
    onClose,
}: Readonly<FormModalContentProps>) {
    const { values, submitForm, isSubmitting } = useFormikContext<FormikValues>();
    const currentField = fields[currentPage];

    if (!currentField) {
        return null;
    }

    const FieldComponent = FIELD_COMPONENT_BY_TYPE[currentField.field_type];
    const value = values[currentField.key];
    const isEmpty = value == null || value === '' || (Array.isArray(value) && value.length === 0);
    const canGoNext = !currentField.required || !isEmpty;

    return (
        <Dialog
            open={open}
            onClose={() => onClose(values)}
            heading={heading}
            size="medium"
            actions={
                <FormModalFooter
                    currentPage={currentPage}
                    totalPages={totalPages}
                    canGoNext={canGoNext}
                    isSubmitting={isSubmitting}
                    onBack={onBack}
                    onNext={() => onNext(values)}
                    onSubmit={submitForm}
                />
            }>
            <Stack spacing={0.5}>
                <LinkifyText variant="h2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {currentField.question}
                </LinkifyText>
                {currentField.description && (
                    <LinkifyText variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {currentField.description}
                    </LinkifyText>
                )}
            </Stack>
            <Box sx={{ mt: 3 }}>
                <FieldComponent field={currentField} />
            </Box>
        </Dialog>
    );
}

export default function FormModal({ open, onClose, task }: Readonly<FormModalProps>) {
    const context = isFormTaskContext(task.context) ? task.context : null;
    const fields = context?.fields ?? [];
    const { currentPage, totalPages, initialResponses, next, back, close, handleSubmit } = useTaskFormSubmit({
        task,
        fields,
        onClose,
    });

    if (!context) {
        return null;
    }

    return (
        <Formik initialValues={initialResponses} onSubmit={handleSubmit}>
            <FormModalContent
                open={open}
                fields={context.fields}
                heading={task.task_template.display_name}
                currentPage={currentPage}
                totalPages={totalPages}
                onNext={next}
                onBack={back}
                onClose={close}
            />
        </Formik>
    );
}
